import { h } from "@stencil/core";
import { isBreakpointSmaller } from "../../../utils";
export class DcUiHeaderNavBreadcrumb {
    constructor() {
        this.menuName = 'Menu';
        this.items = [];
        this.isMobile = this.checkIfMobile();
        this.handleResize = () => {
            this.isMobile = this.checkIfMobile();
        };
        this.handleClick = (index, label, isRoot) => {
            this.breadcrumbClick.emit({ index, label, isRoot });
        };
    }
    checkIfMobile() {
        return isBreakpointSmaller('xl');
    }
    componentDidLoad() {
        window.addEventListener('resize', this.handleResize);
    }
    disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize);
    }
    async addItem(label, isRoot = false, level) {
        // On mobile, preserve the root item by keeping it and only modifying items after it
        if (this.isMobile) {
            // Check for duplicate item (same label and level)
            const isDuplicate = this.items.some((item) => item.label === (isRoot ? this.menuName : label) && item.level === level);
            if (isDuplicate)
                return;
            const rootItem = this.items.find((item) => item.isRoot);
            const nonRootItems = this.items.filter((item) => !item.isRoot);
            if (isRoot) {
                // For root items in mobile, just add them if they don't exist
                if (!rootItem) {
                    this.items = [
                        {
                            label: this.menuName,
                            isCurrent: false,
                            isRoot: true,
                            level: 0,
                        },
                        ...nonRootItems,
                    ];
                }
                return;
            }
            // Rest of the mobile logic for non-root items
            const existingItemIndex = nonRootItems.findIndex((item) => item.level === level);
            if (existingItemIndex !== -1) {
                // Remove all items from this level onwards, but keep the root item
                nonRootItems.splice(existingItemIndex);
            }
            // Remove current flag from previous items
            const updatedNonRootItems = nonRootItems.map((item) => (Object.assign(Object.assign({}, item), { isCurrent: false })));
            // Add new item as current (if it's not a root item)
            if (!isRoot) {
                const newItem = {
                    label,
                    isCurrent: false,
                    isRoot: false,
                    level,
                };
                // Combine root item (if exists) with other items and new item
                this.items = rootItem
                    ? [rootItem, ...updatedNonRootItems, newItem]
                    : [...updatedNonRootItems, newItem];
            }
        }
        else {
            // Check for duplicate item (same label and level)
            const isDuplicate = this.items.some((item) => item.label === label && item.level === level);
            if (isDuplicate)
                return;
            const existingItemIndex = this.items.findIndex((item) => item.level === level);
            if (existingItemIndex !== -1) {
                this.items = this.items.slice(0, existingItemIndex);
            }
            this.items = this.items.map((item) => (Object.assign(Object.assign({}, item), { isCurrent: false })));
            const isRootItemAlreadyAdded = this.items.some((item) => item.isRoot);
            if (isRootItemAlreadyAdded && isRoot)
                return;
            this.items = [
                ...this.items,
                {
                    label: label,
                    isCurrent: isRoot ? false : true,
                    isRoot,
                    level: level,
                },
            ];
        }
    }
    async addRootItem() {
        // On Mobile we need a "special" root item on the first level to go back to the main nav. Default label is "Menu"
        this.addItem(this.menuName, true, 0);
    }
    async removeItem(index) {
        if (index < 0 || index >= this.items.length)
            return;
        const newItems = this.items.filter((_, i) => i !== index);
        // Make the last item current
        if (newItems.length > 0) {
            newItems[newItems.length - 1].isCurrent = true;
        }
        this.items = newItems;
    }
    async removeAllItemsFrom(index) {
        // On desktop, if index is 1, clear all items (including root)
        if (!this.isMobile && index === 1) {
            this.items = [];
            return;
        }
        // Adjust index to preserve root item on mobile
        const startIndex = this.isMobile ? Math.max(1, index) : index;
        // Keep only items before the start index
        this.items = this.items.slice(0, startIndex);
        // Set last item as current on desktop only
        if (!this.isMobile && this.items.length > 0) {
            this.items[this.items.length - 1].isCurrent = true;
        }
    }
    async clear() {
        this.items = [];
    }
    async exportState() {
        return this.items.map((item) => ({
            label: item.label,
            isRoot: item.isRoot,
            level: item.level,
            isCurrent: item.isCurrent,
        }));
    }
    async importState(items) {
        // Clear existing items
        this.clear();
        // Import new items
        items.forEach((item) => {
            this.addItem(item.label, item.isRoot, item.level);
        });
    }
    render() {
        if (this.items.length === 0)
            return null;
        return (h("nav", { class: "breadcrumb", "aria-label": "Breadcrumb" }, h("ol", null, this.items.map((item) => (h("li", { class: {
                'breadcrumb-item': true,
                current: item.isCurrent,
                'mobile-root-item': item.isRoot,
            } }, h("dc-ui-header-nav-breadcrumb-item", { label: item.label, isCurrent: item.isCurrent, isRoot: item.isRoot, level: item.level, onClick: () => !item.isCurrent && this.handleClick(item.level, item.label, item.isRoot) })))))));
    }
    static get is() { return "dc-ui-header-nav-breadcrumb"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["dc-ui-header-nav-breadcrumb.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["dc-ui-header-nav-breadcrumb.css"]
        };
    }
    static get properties() {
        return {
            "menuName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "menu-name",
                "reflect": false,
                "defaultValue": "'Menu'"
            }
        };
    }
    static get states() {
        return {
            "items": {},
            "isMobile": {}
        };
    }
    static get events() {
        return [{
                "method": "breadcrumbClick",
                "name": "breadcrumbClick",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "{ index: number; label: string; isRoot: boolean }",
                    "resolved": "{ index: number; label: string; isRoot: boolean; }",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "addItem": {
                "complexType": {
                    "signature": "(label?: string, isRoot?: boolean, level?: number) => Promise<void>",
                    "parameters": [{
                            "name": "label",
                            "type": "string",
                            "docs": ""
                        }, {
                            "name": "isRoot",
                            "type": "boolean",
                            "docs": ""
                        }, {
                            "name": "level",
                            "type": "number",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "addRootItem": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "removeItem": {
                "complexType": {
                    "signature": "(index: number) => Promise<void>",
                    "parameters": [{
                            "name": "index",
                            "type": "number",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "removeAllItemsFrom": {
                "complexType": {
                    "signature": "(index: number) => Promise<void>",
                    "parameters": [{
                            "name": "index",
                            "type": "number",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "clear": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "exportState": {
                "complexType": {
                    "signature": "() => Promise<{ label: string; isRoot: boolean; level: number; isCurrent: boolean; }[]>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<{ label: string; isRoot: boolean; level: number; isCurrent: boolean; }[]>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "importState": {
                "complexType": {
                    "signature": "(items: BreadcrumbItem[]) => Promise<void>",
                    "parameters": [{
                            "name": "items",
                            "type": "BreadcrumbItem[]",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "BreadcrumbItem": {
                            "location": "local",
                            "path": "D:/a/1/s/packages/components/src/components/dc-ui-header/dc-ui-header-nav-breadcrumb/dc-ui-header-nav-breadcrumb.tsx",
                            "id": "src/components/dc-ui-header/dc-ui-header-nav-breadcrumb/dc-ui-header-nav-breadcrumb.tsx::BreadcrumbItem"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            }
        };
    }
}
//# sourceMappingURL=dc-ui-header-nav-breadcrumb.js.map
